import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// May 14 batch: NextCentury-style prefix + auto-increment unit creator.
// Used by Add Property Step 2 (inline) and the standalone "+ Add Units"
// modal on a Building. The form owns its own preview-row state; the
// parent receives the materialized list via onChange and submits through
// the shared bulk-create endpoint.
//
// The optional Device column auto-pairs a serial-numbered device from
// the org inventory to the new unit at submit time. Empty cell = no
// device. Server validates serials against the inventory + unassigned
// state and surfaces row-level errors on collision.

export interface GeneratedUnit {
  // Stable key per preview row — kept independent of `unitNumber` so the
  // input can be empty without collapsing the row identity.
  key: string;
  unitNumber: string;
  deviceSerial?: string;
  // May 15 batch CSV expansion — carried through but read-only in the
  // preview row. Users editing the bulk form by hand don't get to set
  // these (they'd use the device's edit modal post-create); the CSV
  // path does, and the submit handler propagates them.
  deviceType?: 'WaterMeter' | 'SALTTransmitter';
  multiplier?: string;
  meterSerial?: string;
  imr?: number;
}

export interface RowError {
  // Zero-based index into the rendered preview rows.
  rowIndex: number;
  message: string;
}

interface Props {
  buildingName: string;
  existingUnitNames?: string[];
  startNumber?: number;
  initialCount?: number;
  initialPrefix?: string;
  // Pre-fill the preview from an external source (e.g. CSV upload). When
  // provided, every row starts as an "override" so the user's values
  // never get replaced by auto-increment regeneration on later edits.
  initialUnits?: {
    unitNumber: string;
    deviceSerial?: string;
    deviceType?: 'WaterMeter' | 'SALTTransmitter';
    multiplier?: string;
    meterSerial?: string;
    imr?: number;
  }[];
  // Per-row errors from the server (422 response) — keyed by index into
  // the preview's flat row list (excluding the preceding "building"
  // header row used by the inline Add Property submission).
  externalErrors?: RowError[];
  onChange: (units: GeneratedUnit[]) => void;
}

const MAX_UNITS_PER_BUILDING = 500;

export function BulkUnitsForm({
  buildingName,
  existingUnitNames = [],
  startNumber: startNumberProp = 1,
  initialCount = 10,
  initialPrefix,
  initialUnits,
  externalErrors,
  onChange,
}: Props) {
  const seededFromUnits = initialUnits && initialUnits.length > 0;
  const [count, setCount] = useState<number>(
    seededFromUnits
      ? Math.min(initialUnits!.length, MAX_UNITS_PER_BUILDING)
      : Math.min(initialCount, MAX_UNITS_PER_BUILDING),
  );
  const [prefix, setPrefix] = useState<string>(initialPrefix ?? `${buildingName} `.trimStart());
  const [startNumber, setStartNumber] = useState<number>(startNumberProp);
  const [autoIncrement, setAutoIncrement] = useState(!seededFromUnits);
  const [overrides, setOverrides] = useState<Record<number, Partial<GeneratedUnit>>>(() => {
    if (!seededFromUnits) return {};
    const seed: Record<number, Partial<GeneratedUnit>> = {};
    initialUnits!.forEach((u, i) => {
      seed[i] = {
        unitNumber: u.unitNumber,
        deviceSerial: u.deviceSerial,
        deviceType: u.deviceType,
        multiplier: u.multiplier,
        meterSerial: u.meterSerial,
        imr: u.imr,
      };
    });
    return seed;
  });

  // When the prop-driven defaults change (e.g., the parent recomputed the
  // standalone Add Units start number after fetching existing units),
  // re-seed local state without throwing away user edits to override rows.
  useEffect(() => {
    setStartNumber(startNumberProp);
  }, [startNumberProp]);
  useEffect(() => {
    if (initialPrefix !== undefined) setPrefix(initialPrefix);
  }, [initialPrefix]);

  // Materialize the preview rows from `count + prefix + start + autoIncrement`
  // plus any per-row overrides. Auto-increment off freezes every row to
  // `${prefix}${startNumber}` (the user is essentially building a list of
  // arbitrary names and editing each).
  const previewRows = useMemo<GeneratedUnit[]>(() => {
    const rows: GeneratedUnit[] = [];
    for (let i = 0; i < count; i++) {
      const computedNumber = autoIncrement ? startNumber + i : startNumber;
      const computedName = `${prefix}${computedNumber}`;
      const override = overrides[i];
      rows.push({
        key: `row-${i}`,
        unitNumber: override?.unitNumber ?? computedName,
        deviceSerial: override?.deviceSerial,
        deviceType: override?.deviceType,
        multiplier: override?.multiplier,
        meterSerial: override?.meterSerial,
        imr: override?.imr,
      });
    }
    return rows;
  }, [count, prefix, startNumber, autoIncrement, overrides]);

  // Push the latest preview to the parent on every change. Stable enough
  // for the parent's submit handler to read off props/state.
  useEffect(() => {
    onChange(previewRows);
  }, [previewRows, onChange]);

  // Inline error map — duplicates within preview + collisions with
  // existing units, AND duplicate device serials within the preview.
  // Server-side row errors (e.g., serial-not-found, serial-already-assigned)
  // overlay the client-derived map below.
  const errorsByRow = useMemo(() => {
    const errors: Record<number, string> = {};
    const seenUnit = new Map<string, number>();
    const seenSerial = new Map<string, number>();
    const existingSet = new Set(existingUnitNames.map((n) => n.trim()).filter(Boolean));
    previewRows.forEach((row, idx) => {
      const trimmed = row.unitNumber.trim();
      if (!trimmed) {
        errors[idx] = 'Unit name required.';
      } else {
        if (existingSet.has(trimmed)) errors[idx] = 'Already exists in this building.';
        const prior = seenUnit.get(trimmed);
        if (prior !== undefined) {
          errors[idx] = 'Duplicate unit name within preview.';
          errors[prior] = 'Duplicate unit name within preview.';
        } else {
          seenUnit.set(trimmed, idx);
        }
      }
      const serial = (row.deviceSerial ?? '').trim();
      if (serial) {
        const prior = seenSerial.get(serial);
        if (prior !== undefined) {
          errors[idx] = 'Duplicate device serial within preview.';
          errors[prior] = 'Duplicate device serial within preview.';
        } else {
          seenSerial.set(serial, idx);
        }
      }
    });
    externalErrors?.forEach((e) => {
      if (e.rowIndex >= 0 && e.rowIndex < previewRows.length) {
        errors[e.rowIndex] = e.message;
      }
    });
    return errors;
  }, [previewRows, existingUnitNames, externalErrors]);

  function setRowOverride(idx: number, patch: Partial<GeneratedUnit>) {
    setOverrides((prev) => {
      const next = { ...prev };
      const baseRow = previewRows[idx];
      const existing = next[idx] ?? {
        unitNumber: baseRow.unitNumber,
        deviceSerial: baseRow.deviceSerial,
        deviceType: baseRow.deviceType,
        multiplier: baseRow.multiplier,
        meterSerial: baseRow.meterSerial,
        imr: baseRow.imr,
      };
      next[idx] = { ...existing, ...patch };
      return next;
    });
  }

  function removeRow(idx: number) {
    // Collapse the override map index above `idx` since rows shift up.
    setOverrides((prev) => {
      const next: typeof prev = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k);
        if (ki === idx) return;
        next[ki > idx ? ki - 1 : ki] = v;
      });
      return next;
    });
    setCount((c) => Math.max(0, c - 1));
  }

  function adjustCount(next: number) {
    const clamped = Math.max(0, Math.min(next, MAX_UNITS_PER_BUILDING));
    setCount(clamped);
  }

  const hasErrors = Object.keys(errorsByRow).length > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input
          label="# of units"
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_UNITS_PER_BUILDING}
          value={count}
          onChange={(e) => adjustCount(parseInt(e.target.value || '0', 10))}
        />
        <Input
          label="Prefix"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder={`${buildingName} `.trimStart()}
        />
        <Input
          label="Starting number"
          type="number"
          inputMode="numeric"
          value={startNumber}
          onChange={(e) => setStartNumber(parseInt(e.target.value || '1', 10))}
        />
        <label className="flex items-end gap-2 text-sm text-gray-700 dark:text-gray-300 pb-2">
          <input
            type="checkbox"
            checked={autoIncrement}
            onChange={(e) => setAutoIncrement(e.target.checked)}
            className="w-4 h-4"
          />
          <span>Auto increment</span>
        </label>
      </div>

      {count > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {previewRows.map((row, idx) => {
              const err = errorsByRow[idx];
              // SALT-specific fields are CSV-only for v1 — surfaced
              // here as a read-only subtitle so the user can confirm
              // what's about to be applied to the paired device.
              const extras: string[] = [];
              if (row.deviceType) {
                extras.push(row.deviceType === 'SALTTransmitter' ? 'SALT' : 'Water Meter');
              }
              if (row.multiplier) extras.push(`Mult ${row.multiplier}`);
              if (row.meterSerial) extras.push(`Meter SN ${row.meterSerial}`);
              if (row.imr !== undefined) extras.push(`IMR ${row.imr}`);
              return (
                <li
                  key={row.key}
                  className={cn(
                    'p-2',
                    err && 'bg-error-50 dark:bg-error-500/10',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-8 tabular-nums">{idx + 1}.</span>
                    <Input
                      value={row.unitNumber}
                      onChange={(e) => setRowOverride(idx, { unitNumber: e.target.value })}
                      placeholder="Unit name"
                      error={err}
                      className="flex-1"
                    />
                    <Input
                      value={row.deviceSerial ?? ''}
                      onChange={(e) => setRowOverride(idx, { deviceSerial: e.target.value || undefined })}
                      placeholder="Device serial (optional)"
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="text-gray-400 hover:text-error-600 p-1 rounded transition-colors"
                      title="Remove this row"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {extras.length > 0 && (
                    <div className="pl-10 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5">
                      {extras.map((e) => (
                        <span key={e}>{e}</span>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {count >= MAX_UNITS_PER_BUILDING && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Max {MAX_UNITS_PER_BUILDING} units per building.
        </p>
      )}

      {hasErrors && (
        <p className="text-xs text-error-600 dark:text-error-400">
          Resolve highlighted rows before continuing.
        </p>
      )}

      {/* Convenience action — quickly tack on another N rows without
          retyping the count. */}
      {count < MAX_UNITS_PER_BUILDING && (
        <div>
          <Button type="button" variant="ghost" size="sm" onClick={() => adjustCount(count + 1)}>
            + Add row
          </Button>
        </div>
      )}
    </div>
  );
}

export { MAX_UNITS_PER_BUILDING };
